/**
 * Computes the flags emoji string rendered on a Scheduler job tile.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS:
 * --------------------------------------------------------------------------
 *
 * Extracted from `schedule.context.js` projection loop (the function that
 * sets `newJob.flags`). Extraction allows adversarial regression tests to
 * pin the expedite/distant fallback behavior introduced for #159 so that
 * future edits to the emoji string cannot silently drop the fallback.
 *
 * --------------------------------------------------------------------------
 * THE BUG THIS HELPS PREVENT (#159):
 * --------------------------------------------------------------------------
 *
 * Expedite + Long-Distance flags follow a HYBRID Attributes/FileMaker
 * source-of-truth rule (see CLAUDE.md "Known Pitfalls"):
 *   • If a per-job `Attributes` record exists, the projection loop's
 *     override block syncs `document.expediteFlag → jobAttributes.expedite`.
 *   • If NO Attributes record exists yet (FAV-auto-pre-scheduled status-1.2
 *     jobs that no human has opened in JobsDetailModal), `jobAttributes`
 *     is undefined and the emoji condition silently evaluates falsy —
 *     leaving the tile without 🏃🏽‍♂️ even though FileMaker has
 *     expediteFlag=true.
 *
 * The fix is a nullish-coalescing fallback: read jobAttributes.expedite
 * first, then fall back to document.expediteFlag. Same pattern for distant.
 *
 * Tests that import this helper directly cannot drift from production.
 */

const EMOJI = {
  thanksgivingRush: "🎃",
  christmasRush: "🎄",
  expressShip: "📦",
  furniture: "🪑",
  prescheduled: "📅",
  rush: "⚡️",
  expedite: "🏃🏽‍♂️",
  distant: "✈️",
  imsWarning: "🛑",
};

/**
 * @param {object} newJob - Projected job object (must have jobAttributes already overlaid).
 * @param {object} document - Raw ActiveQueue document (source for FileMaker top-level flags).
 * @returns {string} Concatenated emoji string for the tile's `flags` field.
 */
export function computeFlagsEmojiString(newJob, document) {
  const safeJob = newJob || {};
  const safeDoc = document || {};

  // Expedite + Distant: hybrid Attributes/FileMaker source-of-truth.
  // jobAttributes.expedite ?? document.expediteFlag — nullish-coalescing
  // ensures we fall back to the FileMaker top-level flag when no Attributes
  // record exists yet. See #159 and CLAUDE.md "Known Pitfalls".
  const expedite =
    safeJob.jobAttributes?.expedite ?? safeDoc.expediteFlag;
  const distant =
    safeJob.jobAttributes?.distant ?? safeDoc.distantFlag;

  return (
    `${safeJob.thanksgivingRush === 1 ? EMOJI.thanksgivingRush : ""}` +
    `${safeJob.christmasRush === 1 ? EMOJI.christmasRush : ""}` +
    `${safeJob.expressShip === 1 ? EMOJI.expressShip : ""}` +
    `${safeJob.furniture ? EMOJI.furniture : ""}` +
    `${safeJob.prescheduled ? EMOJI.prescheduled : ""}` +
    `${safeJob.rush ? EMOJI.rush : ""}` +
    `${expedite ? EMOJI.expedite : ""}` +
    `${distant ? EMOJI.distant : ""}` +
    `${safeJob.imsWarning ? EMOJI.imsWarning : ""}`
  );
}

export const FLAGS_EMOJI = EMOJI;
